import { useState, useEffect, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { setDate, subMonths, addMonths, addDays, format, isBefore, isAfter, endOfMonth } from 'date-fns';

interface CreditCardSummary {
  currentInvoiceTotal: number;
  isPaid: boolean;
  loading: boolean;
  dueDate: Date | null;
  isOverdue: boolean;
}

export function useCreditCardSummary(
  accountId: string,
  closingDay: number | null,
  dueDay: number | null,
  refreshKey?: number
): CreditCardSummary {
  const [expenses, setExpenses] = useState(0);
  const [payments, setPayments] = useState(0);
  const [loading, setLoading] = useState(true);

  // Calculate current invoice period - MUST match useCreditCardInvoice logic exactly
  const { periodStart, periodEnd, dueDate } = useMemo(() => {
    const closing = closingDay || 1;
    const due = dueDay || Math.min(closing + 10, 28);
    const today = new Date();
    
    // Find the invoice that should be displayed (same logic as useCreditCardInvoice)
    // Generate periods and find the one that is due next (or most recently due)
    
    // Start by finding the current month's due date
    let targetDueDate = setDate(today, due);
    
    // If today is past the due date, this invoice should still show until paid
    // We want to show the "current" invoice - the one that is due soonest that isn't fully in the past
    // If today is after due date, check if we should show next month's invoice
    
    // Calculate closing date based on relationship between closing and due days
    let closingDate: Date;
    if (due > closing) {
      // Due is after closing in same month
      closingDate = setDate(targetDueDate, closing);
    } else {
      // Due is before closing day, so closing was in previous month
      closingDate = setDate(subMonths(targetDueDate, 1), closing);
    }
    
    // Period starts on previous month's closing day and ends on closing date
    // Previous closing is one month before current closing
    const previousClosingDate = subMonths(closingDate, 1);
    // Handle month length differences - ensure we get the correct day
    const previousClosingDay = Math.min(closing, endOfMonth(previousClosingDate).getDate());
    const start = new Date(previousClosingDate.getFullYear(), previousClosingDate.getMonth(), previousClosingDay);
    const end = closingDate;

    return {
      periodStart: start,
      periodEnd: end,
      dueDate: targetDueDate
    };
  }, [closingDay, dueDay]);

  useEffect(() => {
    if (!accountId) {
      setLoading(false);
      return;
    }

    const fetchInvoiceData = async () => {
      setLoading(true);
      try {
        // Calculate the invoice date for this period (first day of due month)
        const invoiceDate = format(dueDate, 'yyyy-MM-dd');
        const invoiceDateFirstDay = format(dueDate, 'yyyy-MM-01');

        // Single RPC replaces 4 separate queries
        const { data, error } = await supabase.rpc("get_credit_card_invoice_summary", {
          p_account_id: accountId,
          p_invoice_date: invoiceDateFirstDay,
          p_period_start: format(periodStart, 'yyyy-MM-dd'),
          p_period_end: format(periodEnd, 'yyyy-MM-dd'),
          p_payment_end: format(addDays(dueDate, 30), 'yyyy-MM-dd'),
        });

        if (error) throw error;

        const result = data as any;
        setExpenses(Number(result?.expenses || 0));
        setPayments(Number(result?.payments || 0));
      } finally {
        setLoading(false);
      }
    };

    fetchInvoiceData();
  }, [accountId, periodStart, periodEnd, dueDate, refreshKey]);

  const currentInvoiceTotal = expenses - payments;
  const isPaid = currentInvoiceTotal <= 0 && expenses > 0;
  const isOverdue = dueDate ? isBefore(dueDate, new Date()) && currentInvoiceTotal > 0 : false;

  return {
    currentInvoiceTotal,
    isPaid,
    loading,
    dueDate,
    isOverdue
  };
}
